import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { usePagination } from '@/hooks'
import {
  useDeleteWorkItemAttachment,
  useUploadWorkItemAttachments,
  useWorkItemAttachmentDownloadUrl,
  useWorkItemAttachments,
} from '@/hooks/useWorkItemAttachments'
import {
  ACCEPTED_TYPES,
  ALLOWED_EXTENSIONS,
  MAX_ATTACHMENTS,
  MAX_BATCH_SIZE_BYTES,
  MAX_FILE_SIZE_BYTES,
  formatFileSize,
  getFileExtension,
  limitBatchSize,
} from '@/lib/attachments'
import { formatDateTime } from '@/lib/timeFormatters'
import { getInitials } from '@/lib/utils'
import type { WorkItemAttachment } from '@/types'
import { Download, File, FileText, Image, Trash2 } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

interface AttachmentsTabProps {
  projectId: string
  workItemId: string
  canEdit: boolean
}

function getFileIcon(contentType: string, fileName: string) {
  if (contentType.startsWith('image/')) return <Image className="h-4 w-4 text-muted-foreground shrink-0" />
  if (contentType === 'application/pdf' || fileName.endsWith('.pdf')) return <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
  if (
    contentType.includes('word') ||
    contentType.includes('excel') ||
    contentType.includes('powerpoint') ||
    contentType === 'text/plain'
  ) return <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
  return <File className="h-4 w-4 text-muted-foreground shrink-0" />
}

export function AttachmentsTab({ projectId, workItemId, canEdit }: AttachmentsTabProps) {
  const { page, pageSize, paginationParams, setPage, setPageSize } = usePagination()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<WorkItemAttachment | null>(null)

  const { data, isLoading } = useWorkItemAttachments(projectId, workItemId, paginationParams)
  const attachments = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const uploadMutation = useUploadWorkItemAttachments(projectId, workItemId)
  const deleteMutation = useDeleteWorkItemAttachment(projectId, workItemId)
  const downloadMutation = useWorkItemAttachmentDownloadUrl(projectId, workItemId)

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const errors: string[] = []
    const valid: File[] = []

    for (const f of fileArray) {
      const ext = getFileExtension(f.name)
      if (!ALLOWED_EXTENSIONS.has(ext)) {
        errors.push(`"${f.name}" has an unsupported file type.`)
        continue
      }
      if (f.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`"${f.name}" exceeds the 60 MB size limit.`)
        continue
      }
      valid.push(f)
    }

    const slotsRemaining = MAX_ATTACHMENTS - totalCount
    const withinSlots = slotsRemaining > 0 ? valid.slice(0, slotsRemaining) : []
    const rejectedByLimit = slotsRemaining > 0 ? valid.slice(slotsRemaining) : valid

    rejectedByLimit.forEach(f =>
      errors.push(`"${f.name}" could not be uploaded: attachment limit of ${MAX_ATTACHMENTS} reached.`)
    )

    const batch = limitBatchSize(withinSlots, MAX_BATCH_SIZE_BYTES)
    const toUpload = batch.accepted
    errors.push(...batch.errors)

    errors.forEach((msg, i) => setTimeout(() => toast.error(msg), i * 700))

    if (toUpload.length > 0) uploadMutation.mutate(toUpload)
  }, [uploadMutation, totalCount])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDraggingOver(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDraggingOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(false)
  }, [])

  const confirmDelete = () => {
    if (!pendingDelete) return
    deleteMutation.mutate(pendingDelete.id, {
      onSettled: () => setPendingDelete(null),
    })
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-medium text-foreground">Attachments</h2>

      {canEdit && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${isDraggingOver
              ? 'border-primary bg-primary/5'
              : 'border-border bg-border hover:border-border'
            }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          aria-label="Upload attachments drop zone"
          data-testid="attachments-drop-zone"
        >
          {uploadMutation.isPending ? (
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">Uploading...</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Drag your file(s) to start uploading /{' '}
              <span className="font-semibold text-foreground">Browse Files.</span>
            </p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_TYPES}
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files)
              e.target.value = ''
            }}
            data-testid="attachments-file-input"
            aria-label="Select files to upload"
          />
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading attachments">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : totalCount === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="attachments-empty">
          No attachments yet.
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table aria-label="Attachments table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-[100px]">Size</TableHead>
                <TableHead className="w-[190px]">Date Added</TableHead>
                <TableHead className="w-[220px]">Added By</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attachments.map((attachment) => (
                <AttachmentRow
                  key={attachment.id}
                  attachment={attachment}
                  canEdit={canEdit}
                  isDownloading={downloadMutation.isPending}
                  isDeleting={deleteMutation.isPending && pendingDelete?.id === attachment.id}
                  onDownload={() => downloadMutation.mutate(attachment.id)}
                  onDelete={() => setPendingDelete(attachment)}
                />
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      <Dialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete Attachment?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete the attachment{' '}
            <span className="font-semibold text-foreground">
              &ldquo;{pendingDelete?.fileName}&rdquo;
            </span>
            ? This action cannot be undone and the attachment will be permanently removed from the list.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              data-testid="confirm-delete-attachment"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Attachment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface AttachmentRowProps {
  attachment: WorkItemAttachment
  canEdit: boolean
  isDownloading: boolean
  isDeleting: boolean
  onDownload: () => void
  onDelete: () => void
}

function AttachmentRow({ attachment, canEdit, isDownloading, isDeleting, onDownload, onDelete }: AttachmentRowProps) {
  return (
    <TableRow data-testid={`attachment-row-${attachment.id}`}>
      <TableCell>
        <div className="flex items-center gap-2 min-w-0">
          {getFileIcon(attachment.contentType, attachment.fileName)}
          <span className="min-w-0 text-sm">{attachment.fileName}</span>
        </div>
      </TableCell>
      <TableCell className="text-sm">
        {formatFileSize(attachment.sizeBytes)}
      </TableCell>
      <TableCell className="text-sm">
        {formatDateTime(attachment.creationTime)}
      </TableCell>
      <TableCell>
        {attachment.uploaderName && (
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium shrink-0">
              {getInitials(attachment.uploaderName)}
            </div>
            <div className="flex min-w-0 flex-col" data-testid="stacked-identity">
              <span className="text-sm font-medium">{attachment.uploaderName}</span>
              {attachment.uploaderEmail && (
                <span className="text-xs text-muted-foreground">{attachment.uploaderEmail}</span>
              )}
            </div>
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onDownload}
            disabled={isDownloading}
            aria-label={`Download ${attachment.fileName}`}
            data-testid={`download-attachment-${attachment.id}`}
          >
            <Download className="h-4 w-4" />
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              disabled={isDeleting}
              aria-label={`Delete ${attachment.fileName}`}
              data-testid={`delete-attachment-${attachment.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
